import {
  CheckCircle2, Clock, AlertTriangle, Loader2, RefreshCw, ShieldOff, Shield, ShieldCheck
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from "@/components/ui/alert-dialog";
import {
  useIssueSsl, useRenewSsl, useRevokeSsl, useSslCertificates
} from "@/hooks/useSsl";
import { useToast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import { cn, formatDate } from "@/lib/utils";
import type { SslCertificateDto } from "@/types/api";

interface SslTabProps {
  containerId: number;
  domainName: string;
  hasPackage: boolean;
}

export function SslTab({ containerId, domainName, hasPackage }: SslTabProps) {
  const { data, isLoading, error, refetch, isFetching } = useSslCertificates(
    hasPackage ? containerId : undefined
  );
  const issue = useIssueSsl(containerId);
  const { toast } = useToast();

  if (!hasPackage) return <NotProvisioned domainName={domainName} />;
  if (isLoading)   return <Skel />;
  if (error)       return <ErrorCard error={error} />;

  const certs = data ?? [];
  const hasAny  = certs.length > 0;
  const pending = certs.some(c => c.status === "pending");

  async function onIssue() {
    try {
      await issue.mutateAsync({ auto_renew: true });
      toast({
        title: "Issuing SSL",
        description: "Let's Encrypt usually finishes in 1–5 minutes. We'll refresh automatically.",
        variant: "success"
      });
    } catch (err) {
      toast({
        title: "Couldn't start SSL",
        description: err instanceof ApiError ? `${err.message}${err.code ? ` (${err.code})` : ""}` : "Network error.",
        variant: "destructive"
      });
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="font-display text-lg font-semibold">SSL certificates</h2>
          <p className="text-sm text-muted-foreground">
            Let's Encrypt — free, auto-renewing, covers <span className="font-mono">{domainName}</span> + <span className="font-mono">www.</span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void refetch()} disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          {!hasAny && (
            <Button onClick={onIssue} disabled={issue.isPending}>
              {issue.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shield className="h-4 w-4" />}
              Issue Let's Encrypt SSL
            </Button>
          )}
        </div>
      </div>

      {!hasAny ? (
        <EmptyState onIssue={onIssue} issuing={issue.isPending} />
      ) : (
        <div className="grid gap-4">
          {certs.map(cert => (
            <CertCard key={cert.provider_reference} cert={cert} containerId={containerId} />
          ))}
        </div>
      )}

      {pending && (
        <p className="text-xs text-muted-foreground">
          A certificate is pending — this page auto-refreshes every 15 seconds.
        </p>
      )}
    </div>
  );
}

// ── Cert card ──────────────────────────────────────────────
function CertCard({ cert, containerId }: { cert: SslCertificateDto; containerId: number }) {
  const renew = useRenewSsl(containerId);
  const revoke = useRevokeSsl(containerId);
  const { toast } = useToast();

  async function onRenew() {
    try {
      await renew.mutateAsync(cert.provider_reference);
      toast({
        title: "Renewing certificate",
        description: "We'll refresh when 20i finishes turning the new cert.",
        variant: "success"
      });
    } catch (err) {
      toast({
        title: "Renew failed",
        description: err instanceof ApiError ? err.message : "Network error.",
        variant: "destructive"
      });
    }
  }

  async function onRevoke() {
    try {
      await revoke.mutateAsync(cert.provider_reference);
      toast({ title: "Certificate revoked", variant: "success" });
    } catch (err) {
      toast({
        title: "Revoke failed",
        description: err instanceof ApiError ? err.message : "Network error.",
        variant: "destructive"
      });
    }
  }

  const status = normalizeStatus(cert.status);
  const expiresInDays = daysUntil(cert.expires_at);
  const expiringSoon  = status === "active" && expiresInDays != null && expiresInDays < 30;

  return (
    <Card className={cn(
      "overflow-hidden",
      status === "active"  && "border-brand-green/30",
      status === "pending" && "border-brand-gold/40",
      status === "expired" && "border-destructive/40"
    )}>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <StatusIcon status={status} />
          <div>
            <CardTitle className="text-lg">{cert.domain_name || "Certificate"}</CardTitle>
            <CardDescription className="text-xs">{cert.issuer}</CardDescription>
          </div>
        </div>
        <StatusBadge status={status} />
      </CardHeader>
      <CardContent className="space-y-4">
        {status === "pending" ? (
          <p className="rounded-md border border-brand-gold/30 bg-brand-gold/10 px-3 py-2 text-sm text-foreground">
            <Clock className="mr-1.5 inline h-4 w-4 text-brand-gold" />
            Let's Encrypt is verifying ownership. Usually 1–5 minutes.
          </p>
        ) : status === "expired" ? (
          <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-foreground">
            <AlertTriangle className="mr-1.5 inline h-4 w-4 text-destructive" />
            This certificate has expired. Renew or revoke.
          </p>
        ) : null}

        <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
          <Field label="Issued"     value={formatDate(cert.issued_at)} />
          <Field
            label="Expires"
            value={formatDate(cert.expires_at)}
            sub={expiresInDays != null ? `${expiresInDays}d left` : undefined}
            accent={expiringSoon ? "warn" : status === "expired" ? "destructive" : "default"}
          />
          <Field
            label="Auto-renew"
            value={cert.auto_renew ? "On" : "Off"}
            accent={cert.auto_renew ? "ok" : "warn"}
          />
        </div>

        <div className="flex flex-col gap-2 border-t border-border pt-3 sm:flex-row sm:items-center sm:justify-end">
          {status !== "pending" && (
            <Button variant="outline" size="sm" onClick={onRenew} disabled={renew.isPending}>
              {renew.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Renew now
            </Button>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm">
                <ShieldOff className="h-4 w-4 text-destructive" />
                Revoke
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Revoke this certificate?</AlertDialogTitle>
                <AlertDialogDescription>
                  Browsers will start showing warnings for {cert.domain_name} until you issue a new one.
                  Recovery: come back here and click "Issue Let's Encrypt SSL".
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={onRevoke}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >Revoke cert</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Pieces ────────────────────────────────────────────────
function StatusIcon({ status }: { status: "active" | "pending" | "expired" | "unknown" }) {
  const wrap = "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg";
  if (status === "active")  return <div className={cn(wrap, "bg-brand-greenSoft text-brand-green")}><ShieldCheck className="h-5 w-5" /></div>;
  if (status === "pending") return <div className={cn(wrap, "bg-brand-gold/10 text-brand-gold")}><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (status === "expired") return <div className={cn(wrap, "bg-destructive/10 text-destructive")}><AlertTriangle className="h-5 w-5" /></div>;
  return <div className={cn(wrap, "bg-muted text-muted-foreground")}><Shield className="h-5 w-5" /></div>;
}

function StatusBadge({ status }: { status: "active" | "pending" | "expired" | "unknown" }) {
  if (status === "active")  return <Badge variant="success" className="uppercase"><CheckCircle2 className="mr-1 h-3 w-3" />Active</Badge>;
  if (status === "pending") return <Badge variant="outline" className="border-brand-gold/40 bg-brand-gold/10 text-brand-gold uppercase"><Clock className="mr-1 h-3 w-3" />Pending</Badge>;
  if (status === "expired") return <Badge variant="destructive" className="uppercase">Expired</Badge>;
  return <Badge variant="outline" className="uppercase">Unknown</Badge>;
}

function Field({ label, value, sub, accent = "default" }: {
  label: string; value: string; sub?: string;
  accent?: "default" | "ok" | "warn" | "destructive";
}) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={cn(
        "mt-0.5 text-sm font-medium",
        accent === "destructive" && "text-destructive",
        accent === "warn"        && "text-brand-gold",
        accent === "ok"          && "text-brand-green"
      )}>{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
    </div>
  );
}

// ── Empty + states ────────────────────────────────────────
function EmptyState({ onIssue, issuing }: { onIssue: () => void; issuing: boolean }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-accentSoft text-primary">
          <Shield className="h-6 w-6" />
        </div>
        <div>
          <p className="font-display text-lg font-semibold">No SSL yet</p>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            Issue a free Let's Encrypt certificate. Covers the apex + www subdomain, auto-renews every 90 days.
          </p>
        </div>
        <Button onClick={onIssue} disabled={issuing}>
          {issuing && <Loader2 className="h-4 w-4 animate-spin" />}
          Issue Let's Encrypt SSL
        </Button>
      </CardContent>
    </Card>
  );
}

function NotProvisioned({ domainName }: { domainName: string }) {
  return (
    <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">
      {domainName} doesn't have a 20i hosting package yet, so we can't issue SSL.
      Finish the order on HostShop first — the webhook will tag this domain automatically.
    </CardContent></Card>
  );
}

function Skel() {
  return (
    <Card><CardContent className="space-y-3 py-5">
      <Skeleton className="h-6 w-40" />
      <Skeleton className="h-24 w-full" />
    </CardContent></Card>
  );
}

function ErrorCard({ error }: { error: unknown }) {
  const message =
    error instanceof ApiError ? `${error.message} (${error.code ?? error.status})` : "Network error.";
  return (
    <Card><CardContent className="py-8 text-center text-sm text-destructive">
      Couldn't load SSL: {message}
    </CardContent></Card>
  );
}

// ── Helpers ────────────────────────────────────────────────
function normalizeStatus(raw: string): "active" | "pending" | "expired" | "unknown" {
  const v = (raw ?? "").toLowerCase();
  if (v === "active")  return "active";
  if (v === "pending") return "pending";
  if (v === "expired") return "expired";
  return "unknown";
}

function daysUntil(iso?: string): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const ms = d.getTime() - Date.now();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}
